import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForeverOutlined";
import { BookingStatus } from "@repo/database";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import { useMediaQuery } from "~/hooks/use-media-query";
import { cn } from "~/lib/utils";
import { useBookingManager } from "~/managers/use-booking-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { useTempBookingStore } from "~/stores/use-booking-store";
import { Button } from "../ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer";
import {
  SelectContent,
  SelectItem,
  SelectValue,
  SelectWithTrigger,
} from "../ui/select";
import { BookingForm } from "./booking-form";

export default function BookingDialog() {
  const isMobile = useMediaQuery(768);

  const [type, setType] = useState<"block" | "booking">("booking");

  const { checkUnsavedChanges } = useUnsavedChanges();

  const { isLoading, deleteBooking, acceptBooking, declineBooking } =
    useBookingManager();

  const {
    bookingDialogOpen,
    setBookingDialogOpen,
    setSelectedBooking,
    selectedBooking,
  } = useTempBookingStore(
    useShallow((state) => ({
      selectedBooking: state.selectedBooking,
      bookingDialogOpen: state.bookingDialogOpen,
      setBookingDialogOpen: state.setBookingDialogOpen,
      setSelectedBooking: state.setSelectedBooking,
    })),
  );

  const { t } = useTranslation("common");

  const isPendingBooking =
    selectedBooking?.bookingStatus === BookingStatus.pending;

  const handleDialog = (open: boolean, force?: boolean) => {
    if (open === false) {
      if (force !== true) {
        const result = checkUnsavedChanges();
        if (result === true) {
          setBookingDialogOpen(true);
          return;
        }
      }
      setSelectedBooking(undefined);
    }
    setBookingDialogOpen(open);
  };

  const handleDeleteBooking = async () => {
    if (!selectedBooking?.id) return;
    const result = await deleteBooking(selectedBooking.id);
    if (result === true) {
      handleDialog(false, true);
    }
  };

  const handleAcceptBooking = async () => {
    if (!selectedBooking?.id) return;
    const result = await acceptBooking(selectedBooking.id);
    if (result === true) {
      handleDialog(false, true);
    }
  };

  const handleDeclineBooking = async () => {
    if (!selectedBooking?.id) return;
    const result = await declineBooking(selectedBooking.id);
    if (result === true) {
      handleDialog(false, true);
    }
  };

  return (
    <>
      {isMobile && (
        <Drawer
          open={bookingDialogOpen}
          onClose={(preventClose) => {
            if (preventClose) {
              const result = checkUnsavedChanges();
              if (result === true) preventClose();
            }
          }}
          onOpenChange={handleDialog}
        >
          <DrawerContent>
            <DrawerHeader showClose={true} onClose={() => handleDialog(false)}>
              <DrawerTitle>{t("Booking.booking")}</DrawerTitle>
            </DrawerHeader>
            <div className="max-h-[80dvh] min-h-[80dvh] overflow-auto small-scrollbar">
              <BookingForm
                booking={selectedBooking}
                onClose={(force) => handleDialog(false, force)}
              />
            </div>
            <DrawerFooter className="border-t">
              <div className="flex flex-col gap-2">
                <Button
                  disabled={isLoading}
                  form="booking-form"
                  type="submit"
                  className="w-full"
                  variant="success"
                >
                  {selectedBooking?.id ? t("update") : t("create")}
                </Button>
                {selectedBooking?.id && (
                  <div className="flex flex-row gap-2 items-center justify-center">
                    <Button
                      disabled={isLoading}
                      onClick={handleDeleteBooking}
                      className="text-red-500 hover:text-red-500"
                      variant="ghost"
                    >
                      <DeleteForeverIcon />
                      {t("delete_booking")}
                    </Button>
                  </div>
                )}
              </div>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}
      {!isMobile && (
        <div
          className={cn(
            "hide-scrollbar absolute right-0 z-[999] flex h-full w-96 flex-col items-center overflow-y-auto border-l bg-background transition-all translate-x-96",
            bookingDialogOpen && "translate-x-0",
          )}
        >
          {bookingDialogOpen && (
            <>
              <div className="flex flex-row items-center justify-between w-full shadow border-b gap-4 h-14 px-4">
                <SelectWithTrigger
                  value={type}
                  onValueChange={(value) => setType(value)}
                >
                  <SelectValue />
                  <SelectContent>
                    <SelectItem value="booking">
                      {t("Booking.booking")}
                    </SelectItem>
                    <SelectItem value="block">{t("Booking.block")}</SelectItem>
                  </SelectContent>
                </SelectWithTrigger>
                {/* <Label className="text-xl">{t("Booking.booking")}</Label> */}
                <Button
                  onClick={() => handleDialog(false)}
                  className="p-1"
                  variant="ghost"
                >
                  <CloseIcon />
                </Button>
              </div>
              <div className="overflow-auto small-scrollbar w-full flex-1">
                <BookingForm
                  booking={selectedBooking}
                  onClose={(force) => handleDialog(false, force)}
                  isBlock={type === "block" || selectedBooking?.groupSize === 0}
                />
              </div>
              {isPendingBooking && (
                <div className="flex flex-row gap-2 p-2 w-full">
                  <Button
                    onClick={handleAcceptBooking}
                    size="lg"
                    className="w-full text-xl"
                    variant="success"
                  >
                    {t("Booking.accept")}
                  </Button>
                  <Button
                    onClick={handleDeclineBooking}
                    size="lg"
                    variant="destructive"
                    className="text-xl"
                  >
                    {t("Booking.decline")}
                  </Button>
                </div>
              )}
              {!isPendingBooking && (
                <div className="flex flex-col gap-2 p-2 h-20 justify-center w-full border-t">
                  <Button
                    disabled={isLoading}
                    form="booking-form"
                    type="submit"
                    size="lg"
                    className="w-full text-lg font-extrabold"
                    variant="success"
                  >
                    {selectedBooking?.id ? t("update") : t("create")}
                  </Button>
                  {/* {selectedBooking?.id && (
                    <div className="flex flex-row gap-2 items-center justify-center">
                      <Button
                        disabled={isLoading}
                        onClick={handleDeleteBooking}
                        className="text-red-500 hover:text-red-500"
                        variant="ghost"
                      >
                        <DeleteForeverIcon />
                        {t("delete_booking")}
                      </Button>
                    </div>
                  )} */}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
